var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { memo, useCallback, useEffect, useMemo } from "react";
import { useLocation, useParams } from "react-router-dom";
import { Steps, useSteps } from "react-step-builder";
import { debounce, DEVICE_SERIAL_NUMBER_QUERY_PARAM, getIsOfflineRegistrationAvForced, isNonVerifiedOfflineAvVariant, isOfflineRegistrationVariant, } from "@jti/fe-common";
import { dispatchTrackingEvent, TrackingEvents } from "@jti/tracker";
import { DiscreteHeaderBanner, Form, Loading, Switch, useConfig, useTranslations, useWindowSearchQuery } from "@jti/ui";
import { DeviceCode } from "../../../components/DeviceCode/DeviceCode";
import Stepper from "../../../components/Stepper";
import { useAemModel } from "../../../contexts/AemModelContext";
import { useDeviceRegistrationModel } from "../../../hooks/useDeviceRegistrationModel/useDeviceRegistrationModel";
import { useHiddenAgeVerification } from "../../../hooks/useHiddenAgeVerification";
import { useSearchParam } from "../../../hooks/useSearchParam";
import { useSelector } from "../../../store";
import { useRegistrationForm } from "./hooks/useRegistrationForm/useRegistrationForm";
import { ReferralCode } from "./ReferralCode/ReferralCode";
import { getRegistrationSteps } from "./utils/getRegistrationSteps/getRegistrationSteps";
import AgeVerificationPage from "./AgeVerificationPage";
import { AGE_VERIFICATION_STEP, REFERRAL_STEP, USER_DETAILS_STEP } from "./constants";
import DeviceRegistration from "./DeviceRegistration";
import LoginCredential from "./LoginCredential";
import PersonalDetailsPage from "./PersonalDetails";
import styles from "./NewRegistrationForm.module.scss";
const NewRegistrationForm = () => {
    const { __ } = useTranslations();
    const { REGISTRATION_PHONE_VERIFICATION_ENABLED, REGISTRATION_DEVICE_ENABLED, REGISTRATION_AGE_VERIFICATION_ENABLED, REGISTRATION_AGE_VERIFICATION_OPTIONAL, REGISTRATION_EMAIL_VERIFICATION_ENABLED, FAQ_PAGE, } = useConfig();
    const isPhoneOTPenabled = REGISTRATION_PHONE_VERIFICATION_ENABLED === "true";
    const isDeviceRegistrationEnabled = REGISTRATION_DEVICE_ENABLED === "true";
    const isAgeEnabled = REGISTRATION_AGE_VERIFICATION_ENABLED === "true";
    const isAgeOptional = REGISTRATION_AGE_VERIFICATION_OPTIONAL === "true";
    const isEmailOTPenabled = REGISTRATION_EMAIL_VERIFICATION_ENABLED === "true";
    const isReferralOn = useSelector((state) => state.referral.isActive);
    const deviceSerialFromUrl = useSearchParam(DEVICE_SERIAL_NUMBER_QUERY_PARAM);
    const { next, current, isLast: isLastStepFromList, jump, total } = useSteps();
    const { search } = useLocation();
    const { step: stepFromUrl } = useParams();
    const { isDeviceRegistrationByLeafletEnabled, isDeviceRegistrationMandatory: isDeviceRegistrationMandatoryFromModel, } = useDeviceRegistrationModel();
    const { code } = useWindowSearchQuery();
    const { offlineRegistration } = useAemModel();
    const socialFlow = new URLSearchParams(search).get("flow");
    const isSocialFlow = !!socialFlow;
    const isKakaoFlow = socialFlow === "kakao";
    const { form, isSubmitting, isUserCreated, register, pushToUserQrCode } = useRegistrationForm({
        registrationFlowName: socialFlow || "regular",
        code: typeof code === "string" && code.length > 0 && isSocialFlow ? code : "",
        currentStep: current,
        totalSteps: total,
        isDobInInputMode: true,
        nextStep: next,
    });
    const { isAgeVerificationHidden, isAgeVerificationHiddenLoading } = useHiddenAgeVerification();
    const isDeviceRegistrationMandatory = isDeviceRegistrationMandatoryFromModel && isDeviceRegistrationEnabled;
    const isAgeVerificationEnabled = (isAgeEnabled && !isAgeVerificationHidden && !isOfflineRegistrationVariant()) ||
        (isNonVerifiedOfflineAvVariant() && Boolean(offlineRegistration === null || offlineRegistration === void 0 ? void 0 : offlineRegistration.isAvEnabled)) ||
        getIsOfflineRegistrationAvForced();
    const stepperData = useMemo(() => {
        const isDeviceRegistrationHidden = !isDeviceRegistrationMandatory && !!isDeviceRegistrationByLeafletEnabled && !!deviceSerialFromUrl;
        return getRegistrationSteps({
            isDeviceRegistrationEnabled: (isDeviceRegistrationEnabled && !isDeviceRegistrationHidden && !isOfflineRegistrationVariant()) ||
                (isNonVerifiedOfflineAvVariant() && Boolean(offlineRegistration === null || offlineRegistration === void 0 ? void 0 : offlineRegistration.isDeviceRegistrationEnabled)),
            isAgeEnabled,
            isAgeOptional,
            isSocialFlow,
            isAgeVerificationEnabled,
            isDeviceRegistrationMandatory,
            isReferralOn,
        });
    }, [
        isDeviceRegistrationMandatory,
        isDeviceRegistrationByLeafletEnabled,
        deviceSerialFromUrl,
        isDeviceRegistrationEnabled,
        offlineRegistration === null || offlineRegistration === void 0 ? void 0 : offlineRegistration.isDeviceRegistrationEnabled,
        isAgeEnabled,
        isAgeOptional,
        isSocialFlow,
        isAgeVerificationEnabled,
        isReferralOn,
    ]);
    const dispatchGtmStepEvent = useCallback(() => {
        var _a;
        const currentStep = current - 1;
        dispatchTrackingEvent(TrackingEvents.TrackingSignUpProgress, {
            sign_up_step_name: (_a = stepperData === null || stepperData === void 0 ? void 0 : stepperData.titles[currentStep]) === null || _a === void 0 ? void 0 : _a.title,
            sign_up_step_number: `${current}`,
            from_kakao: isKakaoFlow ? 1 : 0,
            use_referral_code: form.getValues("referral_code") ? "yes" : "no",
        });
    }, [current, form, isKakaoFlow, stepperData === null || stepperData === void 0 ? void 0 : stepperData.titles]);
    useEffect(() => {
        window.scrollTo({
            top: 0,
            left: 0,
            behavior: "smooth",
        });
    }, [current]);
    useEffect(() => {
        if (document.readyState === "complete") {
            dispatchGtmStepEvent();
        }
        else {
            window.addEventListener("load", dispatchGtmStepEvent);
            return () => {
                window.removeEventListener("load", dispatchGtmStepEvent);
            };
        }
    }, [current, dispatchGtmStepEvent]);
    useEffect(() => {
        if (total > 0 && stepFromUrl) {
            jump(+stepFromUrl);
        }
    }, [stepFromUrl, total]);
    const isLast = useMemo(() => {
        const getLastStepNumber = () => {
            if (isDeviceRegistrationMandatory) {
                return total;
            }
            if (isSocialFlow) {
                return REFERRAL_STEP;
            }
            if (!isAgeVerificationEnabled) {
                return USER_DETAILS_STEP;
            }
            if (isAgeVerificationEnabled) {
                return AGE_VERIFICATION_STEP;
            }
            return USER_DETAILS_STEP;
        };
        return current === getLastStepNumber();
    }, [current, isDeviceRegistrationMandatory, isSocialFlow, isAgeVerificationEnabled, total]);
    const handleFormSubmit = useCallback((data) => __awaiter(void 0, void 0, void 0, function* () {
        if (!isLast) {
            return next();
        }
        return yield register(data);
    }), [isLast, register, next]);
    const checkKeyDown = useCallback((event) => {
        if (event.key === "Enter") {
            event.preventDefault();
        }
    }, []);
    const onSubmit = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        yield form.handleSubmit(handleFormSubmit)();
    }), [form, handleFormSubmit, next, isLast]);
    const handleChangeCode = (code) => {
        form.setValue("referral_code", code);
    };
    const renderStepper = () => {
        if (isAgeVerificationHiddenLoading) {
            return _jsx(Loading, { dataTestId: "registerFormLoader" });
        }
        return (_jsxs(Form, { form: form, onSubmit: handleFormSubmit, onKeyDown: checkKeyDown, dataTestId: "registerForm", className: styles.form, children: [current !== 4 &&
                    deviceSerialFromUrl &&
                    isDeviceRegistrationByLeafletEnabled &&
                    !isDeviceRegistrationMandatory && (_jsx("div", { className: styles.deviceCodeWrapper, children: _jsx(DeviceCode, { code: deviceSerialFromUrl }) })), _jsx(Stepper, { titles: stepperData.titles, canMoveToStep: !isUserCreated, children: _jsx("div", { className: styles.contentContainer, children: _jsx(Steps, { children: stepperData.steps.map((step) => (_jsxs(Switch, { test: step.component, children: [_jsx(LoginCredential, { onNext: onSubmit, onChangeReferralCode: debounce(handleChangeCode), isEmailOtpEnabled: isEmailOTPenabled, value: "LoginCredential" }), _jsx(ReferralCode, { isLast: isLast, onChangeReferralCode: debounce(handleChangeCode), onNext: onSubmit, isSubmitting: isSubmitting, currentStep: current, value: "ReferralCode" }), _jsx(PersonalDetailsPage, { onNext: onSubmit, isLast: isLast, isOTPenabled: isPhoneOTPenabled, isSubmitting: isSubmitting, value: "PersonalDetailsPage", isAgeVerificationEnabled: isAgeVerificationEnabled }), _jsx(AgeVerificationPage, { onNext: onSubmit, isLast: isLast, isOptional: isAgeOptional, isSubmitting: isSubmitting, value: "AgeVerification", showDeviceRegistrationStep: !isLastStepFromList, isDeviceCodeFromUrlExists: !!isDeviceRegistrationByLeafletEnabled && !!deviceSerialFromUrl }), _jsx(DeviceRegistration, { value: "DeviceRegistration", onNext: onSubmit, isSubmitting: isSubmitting, isLast: isLast, current: current, prefilledValue: deviceSerialFromUrl, pushToUserQrCode: pushToUserQrCode })] }, step.component))) }) }) })] }));
    };
    return (_jsxs("div", { children: [_jsx(DiscreteHeaderBanner, { title: __("auth.registrationpage_title.title"), description: __("auth.registration.page_title.sub_title"), linkHref: FAQ_PAGE, linkLabel: __("auth.registration.page_title.learn_more") }), _jsx("div", { className: styles.container, children: renderStepper() })] }));
};
export default memo(NewRegistrationForm);
